import React, { useEffect, useRef } from 'react';
import { useAuth, hasRole } from '@lb-classroom/feature-auth';
import { useRouter } from 'next/router';
import AppLoading from '../components/UI/atoms/AppLoading';
import { i18n } from 'next-i18next';

/**
 * Gets the appropriate dashboard URL for a user based on their role.
 * Prioritizes Tutor dashboard for users with both roles (admins).
 */
function getDashboardForRole(profileType: string | string[] | undefined): {
  path: string;
  useLocale: boolean;
} {
  const isTutor = hasRole(profileType, 'Tutor');
  const isStudent = hasRole(profileType, 'Student');

  // Tutors (including admins with both roles) go to tutor dashboard
  if (isTutor) {
    return { path: '/t/dashboard', useLocale: false };
  }

  // Students go to student dashboard with locale
  if (isStudent) {
    return { path: '/s/dashboard', useLocale: true };
  }

  // Unknown role, stay on current page (shouldn't happen in normal flow)
  return { path: '/', useLocale: false };
}

function GuestConstraintRoute(WrappedComponent) {
  const GuestConstraint = (props) => {
    const router = useRouter();
    const { currentUser, loading } = useAuth();
    const hasRedirected = useRef(false);

    useEffect(() => {
      // Prevent duplicate redirects
      if (hasRedirected.current) return;

      if (currentUser) {
        hasRedirected.current = true;
        const { path, useLocale } = getDashboardForRole(
          currentUser.profile_type,
        );

        if (useLocale && currentUser.locale?.i18n_form) {
          i18n.changeLanguage(currentUser.locale.i18n_form);
          router.push(path, path, {
            locale: currentUser.locale.i18n_form,
          });
        } else {
          router.push(path);
        }
      }
    }, [currentUser, router]);

    if (loading) return <AppLoading />;
    return <WrappedComponent {...props} />;
  };
  return GuestConstraint;
}

export default GuestConstraintRoute;
